let globalTimezone = 'Asia/Jakarta';

export function setGlobalTimezone(tz) {
  if (tz) globalTimezone = tz;
}

export function getUserTimezone() {
  return globalTimezone;
}

/**
 * Format ISO timestamp menjadi relatif + jam absolut berdasarkan timezone user.
 * Contoh: "5 menit lalu (08:20)", "2 jam lalu", "3 hari lalu, 02/05 08:20"
 */
export function formatRelativeTime(isoString) {
  if (!isoString) return null;
  const tz = getUserTimezone();
  // Pastikan string selalu diparsing sebagai UTC (tambahkan 'Z' jika belum ada)
  const normalized = typeof isoString === 'string' && !isoString.endsWith('Z') && !isoString.includes('+')
    ? isoString + 'Z'
    : isoString;
  const date = new Date(normalized);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  const timeStr = date.toLocaleTimeString('id-ID', {
    hour: '2-digit', minute: '2-digit', timeZone: tz,
  });
  const dateStr = date.toLocaleDateString('id-ID', {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: tz,
  });

  if (diffMins < 1) return `Baru saja (${timeStr})`;
  if (diffMins < 60) return `${diffMins} menit lalu (${timeStr})`;
  if (diffHours < 24) return `${diffHours} jam lalu (${timeStr})`;
  if (diffDays < 7) return `${diffDays} hari lalu, ${dateStr} ${timeStr}`;
  if (diffWeeks < 5) return `${diffWeeks} minggu lalu, ${dateStr}`;
  if (diffMonths < 12) return `${diffMonths} bulan lalu, ${dateStr}`;
  return `${dateStr} ${timeStr}`;
}

/**
 * Konversi string tanggal yang mengandung bulan bahasa Inggris menjadi bahasa Indonesia.
 * Contoh: "30 May 2026" → "30 Mei 2026", "12 January 2026" → "12 Januari 2026"
 * Juga menangani format pendek: "Jan" → "Jan", "Feb" → "Feb", dsb.
 */
const BULAN_MAP = {
  'January': 'Januari', 'February': 'Februari', 'March': 'Maret',
  'April': 'April', 'May': 'Mei', 'June': 'Juni',
  'July': 'Juli', 'August': 'Agustus', 'September': 'September',
  'October': 'Oktober', 'November': 'November', 'December': 'Desember',
  'Jan': 'Jan', 'Feb': 'Feb', 'Mar': 'Mar', 'Apr': 'Apr',
  'Jun': 'Jun', 'Jul': 'Jul', 'Aug': 'Agt', 'Sep': 'Sep',
  'Oct': 'Okt', 'Nov': 'Nov', 'Dec': 'Des',
};

const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agt', 'Sep', 'Okt', 'Nov', 'Des'];
const MONTH_NAMES_FULL = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

export function formatTanggalIndo(input, options = {}) {
  if (!input) return '-';

  if (input instanceof Date) {
    if (isNaN(input.getTime())) return '-';
    const day = input.getDate();
    const month = MONTH_NAMES_SHORT[input.getMonth()];
    const year = input.getFullYear();
    const hours = String(input.getHours()).padStart(2, '0');
    const mins = String(input.getMinutes()).padStart(2, '0');
    if (options.withTime || (input.getHours() !== 0 || input.getMinutes() !== 0 || input.getSeconds() !== 0)) {
      return `${day} ${month} ${year}, ${hours}:${mins} WIB`;
    }
    return `${day} ${month} ${year}`;
  }

  let str = String(input).trim();
  if (!str) return '-';

  // 1. Raw JS Date string like "Fri Aug 28 2026 11:42:53 GMT+0700..." or "Fri Agt 28 2026..."
  if (/^[A-Za-z]{3}\s+[A-Za-z]{3}\s+\d{1,2}\s+\d{4}\s+\d{2}:\d{2}:\d{2}/.test(str)) {
    let parseable = str.replace('Agt', 'Aug').replace('Mei', 'May').replace('Okt', 'Oct').replace('Des', 'Dec');
    const d = new Date(parseable);
    if (!isNaN(d.getTime())) {
      const day = d.getDate();
      const month = MONTH_NAMES_SHORT[d.getMonth()];
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${mins} WIB`;
    }
  }

  // 2. ISO timestamp: 2026-08-28T04:42:53.000Z or 2026-08-28T11:42:53
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) {
    const normalized = (!str.endsWith('Z') && !str.includes('+')) ? str + 'Z' : str;
    const d = new Date(normalized);
    if (!isNaN(d.getTime())) {
      const day = d.getDate();
      const month = MONTH_NAMES_SHORT[d.getMonth()];
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${mins} WIB`;
    }
  }

  // 3. Simple YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-');
    return `${parseInt(d, 10)} ${MONTH_NAMES_FULL[parseInt(m, 10) - 1]} ${y}`;
  }

  // 4. Date string that already has Indonesian format or English words
  str = str.replace(/^(Senin|Selasa|Rabu|Kamis|Jumat|Jum ?at|Sabtu|Minggu|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)(,?\s+)/i, '');
  for (const [en, id] of Object.entries(BULAN_MAP)) {
    str = str.replace(new RegExp(`\\b${en}\\b`, 'g'), id);
  }
  return str;
}